import { z } from 'zod'

// ============================================================================
// SPLIT ORDER TYPES
// ============================================================================

// Input for a single line in a split
export const SplitLineInputSchema = z.object({
  order_line_id: z.string().uuid(),
  line_id: z.number(), // ERP line number for display
  quantity: z.number().positive(),
  description: z.string().optional(),
})

export type SplitLineInput = z.infer<typeof SplitLineInputSchema>

// Input for creating a split order (delivery)
export const SplitOrderInputSchema = z.object({
  order_id: z.number(),
  lines: z.array(SplitLineInputSchema).min(1),
  location: z.string().min(1),
  comment: z.string().optional(),
  is_important_message: z.boolean().optional(),
  photo_url: z.string().url().optional(),
})

export type SplitOrderInput = z.infer<typeof SplitOrderInputSchema>

// Order line with remaining quantity info
export const OrderLineWithRemainingSchema = z.object({
  id: z.string().uuid(),
  line_id: z.number(),
  article_no: z.string().nullable(),
  description: z.string().nullable(),
  count: z.number(),
  delivered_count: z.number(),
  remaining: z.number(), // count - delivered_count
})

export type OrderLineWithRemaining = z.infer<typeof OrderLineWithRemainingSchema>

// Delivery summary for PartialOrderView
export const DeliverySummarySchema = z.object({
  id: z.string().uuid(),
  status: z.enum(['draft', 'ready', 'pending', 'delivered', 'failed']),
  location: z.string().nullable(),
  delivered_at: z.string().nullable(),
  created_at: z.string(),
  created_by: z.string().nullable(),
  photo_url: z.string().nullable(),
  import_comment: z.string().nullable(),
  line_count: z.number(),
  /** Order line IDs that belong to this delivery */
  order_line_ids: z.array(z.string().uuid()),
  /** Map of order_line_id -> quantity in this delivery */
  line_quantities: z.record(z.string().uuid(), z.number()),
  /** Name of person who performed the delivery */
  delivered_by: z.string().nullable(),
  /** Name of recipient (from ID verification) */
  delivered_to_name: z.string().nullable(),
  /** Phone of recipient */
  delivered_to_phone: z.string().nullable(),
})

export type DeliverySummary = z.infer<typeof DeliverySummarySchema>

// Full partial order data for PartialOrderView
export const PartialOrderDataSchema = z.object({
  order: z.object({
    id: z.string().uuid(),
    order_id: z.number(),
    customer_name: z.string().nullable(),
    customer_id: z.string().nullable(),
    customer_phone: z.string().nullable(),
    status: z.string(),
    external_ref: z.string().nullable(),
    reference: z.string().nullable(),
  }),
  pending_deliveries: z.array(DeliverySummarySchema),
  delivered_deliveries: z.array(DeliverySummarySchema),
  remaining_lines: z.array(OrderLineWithRemainingSchema),
  all_lines: z.array(OrderLineWithRemainingSchema),
})

export type PartialOrderData = z.infer<typeof PartialOrderDataSchema>
